import React from 'react';
import { Collapse } from 'antd';
import css from './index.module.scss';
import { useGlobal } from '@/utils/useGlobal';
import EFArrowRight from '@/components/UI/svg/Icons/EFArrowRight';
import EFArrowRightUp from '@/components/UI/svg/Icons/EFArrowRightUp';

const linkMap: { [key: string]: string } = {
    us: 'https://account.ecoflow.com/agreement/en-us/EcoCredits-Rewards-Terms-Conditions.html',
    ca: 'https://account.ecoflow.com/agreement/en-ca/EcoCredits-Rewards-Terms-Conditions.html',
    au: 'https://account.ecoflow.com/agreement/en-au/EcoCredits-Rewards-Terms-Conditions.html',
    eu: 'https://account.ecoflow.com/agreement/en-eu/EcoCredits-Rewards-Terms-Conditions.html',
    uk: 'https://account.ecoflow.com/agreement/en-uk/EcoCredits-Rewards-Terms-Conditions.html',
    de: 'https://account.ecoflow.com/agreement/de-de/EcoCredits-Rewards-Terms-Conditions.html',
    fr: 'https://account.ecoflow.com/agreement/fr-fr/EcoCredits-Rewards-Terms-Conditions.html',
    it: 'https://account.ecoflow.com/agreement/it-it/EcoCredits-Rewards-Terms-Conditions.html',
    es: 'https://account.ecoflow.com/agreement/es-es/EcoCredits-Rewards-Terms-Conditions.html',
    jp: 'https://account.ecoflow.com/agreement/ja-jp/EcoCredits-Rewards-Terms-Conditions.html',
    kr: 'https://account.ecoflow.com/agreement/ko-kr/EcoCredits-Rewards-Terms-Conditions.html',
    za: 'https://account.ecoflow.com/agreement/en-za/EcoCredits-Rewards-Terms-Conditions.html',
};

const guideLinkMap: { [key: string]: string } = {
    us: 'https://us.ecoflow.com/pages/rewards-guide',
    ca: 'https://ca.ecoflow.com/pages/rewards-guide',
    au: 'https://au.ecoflow.com/pages/rewards-guide',
    eu: 'https://eu.ecoflow.com/pages/rewards-guide',
    uk: 'https://uk.ecoflow.com/pages/rewards-guide',
    de: 'https://de.ecoflow.com/pages/rewards-guide',
    fr: 'https://fr.ecoflow.com/pages/rewards-guide',
    it: 'https://it.ecoflow.com/pages/rewards-guide',
    es: 'https://es.ecoflow.com/pages/rewards-guide',
    jp: 'https://jp.ecoflow.com/pages/rewards-guide',
    kr: 'https://kr.ecoflow.com/pages/rewards-guide',
    za: 'https://za.ecoflow.com/pages/rewards-guide',
};

const emailMap: { [key: string]: string } = {
    us: 'support@ecoflow.com',
    eu: 'support.eu@ecoflow.com',
    uk: 'support.eu@ecoflow.com',
    de: 'support.eu@ecoflow.com',
    fr: 'support.eu@ecoflow.com',
    it: 'support.eu@ecoflow.com',
    es: 'support.eu@ecoflow.com',
    jp: 'hello.jp@ecoflow.com',
    au: 'support.au@ecoflow.com',
    za: 'support.za@ecoflow.com',
    kr: 'support.kr@ecoflow.com',
};

const { Panel } = Collapse;
const Faq: React.FC = () => {
    const { useLang, Link, useRouter } = useGlobal();
    const { locale } = useRouter();
    const { t } = useLang('ecocredits');
    const data = [
        {
            key: '1',
            title: t('membership_faq_title'),
            list: [
                {
                    key: '11',
                    quest: t('membership_faq_q_1'),
                    ask: t('membership_faq_a_1'),
                },
                !['jp'].includes(locale) && {
                    key: '12',
                    quest: t('membership_faq_q_2'),
                    ask: t('membership_faq_a_2'),
                },
                {
                    key: '13',
                    quest: t('membership_faq_q_3'),
                    ask: t.rich('membership_faq_a_3', {
                        code: (text: string) => <Link href={guideLinkMap[locale] || guideLinkMap['us']}>{text}</Link>,
                    }),
                },
                {
                    key: '14',
                    quest: t('membership_faq_q_4'),
                    ask: t('membership_faq_a_4'),
                },
                {
                    key: '15',
                    quest: t('membership_faq_q_5'),
                    ask: t('membership_faq_a_5'),
                },
                {
                    key: '16',
                    quest: t('membership_faq_q_6'),
                    ask: t('membership_faq_a_6'),
                },
                {
                    key: '17',
                    quest: t('membership_faq_q_7'),
                    ask: t('membership_faq_a_7'),
                },
            ],
        },
        {
            key: '2',
            title: t('earning_points'),
            list: [
                {
                    key: '21',
                    quest: t('earning_faq_q_1'),
                    ask: t('earning_faq_a_1'),
                },
                {
                    key: '22',
                    quest: t('earning_faq_q_2'),
                    ask: t('earning_faq_a_2'),
                },
                {
                    key: '23',
                    quest: t('earning_faq_q_3'),
                    ask: t('earning_faq_a_3'),
                },
                {
                    key: '24',
                    quest: t('earning_faq_q_4'),
                    ask: t('earning_faq_a_4'),
                },
                {
                    key: '25',
                    quest: t('earning_faq_q_5'),
                    ask: t('earning_faq_a_5'),
                },
                {
                    key: '26',
                    quest: t('earning_faq_q_6'),
                    ask: t('earning_faq_a_6'),
                },
                {
                    key: '27',
                    quest: t('earning_faq_q_7'),
                    ask: t('earning_faq_a_7'),
                },
                {
                    key: '28',
                    quest: t('earning_faq_q_8'),
                    ask: t('earning_faq_a_8'),
                },
                {
                    key: '29',
                    quest: t('earning_faq_q_9'),
                    ask: t('earning_faq_a_9'),
                },
            ],
        },
        {
            key: '3',
            title: t('redeeming_points'),
            list: [
                {
                    key: '31',
                    quest: t('redeeming_faq_q_1'),
                    ask: t('redeeming_faq_a_1'),
                },
                {
                    key: '32',
                    quest: t('redeeming_faq_q_2'),
                    ask: t.rich('redeeming_faq_a_2', {
                        link: () => <Link href={`https://${locale}.ecoflow.com/pages/track-order`}>{`https://${locale}.ecoflow.com/pages/track-order`}</Link>,
                    }),
                },
                {
                    key: '33',
                    quest: t('redeeming_faq_q_3'),
                    ask: t('redeeming_faq_a_3'),
                },
                {
                    key: '34',
                    quest: t('redeeming_faq_q_4'),
                    ask: t.rich('redeeming_faq_a_4', {
                        email: () => {
                            const email = emailMap[locale] || emailMap['us'];
                            return <a href={`mailto:${email}`}>{email}</a>;
                        },
                    }),
                },
                {
                    key: '35',
                    quest: t('redeeming_faq_q_5'),
                    ask: t('redeeming_faq_a_5'),
                },
                {
                    key: '36',
                    quest: t('redeeming_faq_q_6'),
                    ask: t('redeeming_faq_a_6'),
                },
                {
                    key: '37',
                    quest: t('redeeming_faq_q_7'),
                    ask: t.rich('redeeming_faq_a_7', {
                        email: () => {
                            const email = emailMap[locale] || emailMap['us'];
                            return <a href={`mailto:${email}`}>{email}</a>;
                        },
                    }),
                },
                {
                    key: '38',
                    quest: t('redeeming_faq_q_8'),
                    ask: t('redeeming_faq_a_8'),
                },
                {
                    key: '39',
                    quest: t('redeeming_faq_q_9'),
                    ask: t('redeeming_faq_a_9'),
                },
            ],
        },
    ];

    return (
        <div className={css.faq} id="ECO_FAQ">
            <h5 className="faq_title">
                <span className="title-text">{t('faq_title')}</span>
            </h5>
            <Collapse className="faq-collapse" bordered={false} ghost expandIcon={() => <EFArrowRight />}>
                {data?.map((item) => (
                    <Panel header={item.title} key={item.key}>
                        {item?.list
                            ?.filter((faq) => !!faq)
                            ?.map((faq, i) => (
                                <div key={faq.key} className="faq_list">
                                    <h5 className="faq_q">
                                        <span className="order">{i + 1}. </span>
                                        <span className="faq_q_text">{faq.quest}</span>
                                    </h5>
                                    <p className="faq_a">{faq.ask}</p>
                                </div>
                            ))}
                    </Panel>
                ))}
            </Collapse>
            <div className="t_c_content">
                <Link href={linkMap[locale] || linkMap['us']}>
                    <p className="t_c_text">
                        <span className="t">{t('see_t_c', { url: 'xxx' })}</span>
                        <span className="t_icon">
                            <EFArrowRightUp />
                        </span>
                    </p>
                </Link>
            </div>
        </div>
    );
};

export default Faq;
